// HPRMS industrial GRN / QC / Purchase Return / Vendor Bill / Vendor Payment — POS facade paths
import apiClient from "../utilities/apiClient";

const STOCK = "/PurchaseStock";
const INV = "/InvoiceManagement";
const PAY = "/PaymentManagement";

const toArray = (data) => {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

const getId = (entity) => entity?.POID ?? entity?.id ?? null;

const isInvalidId = (id) => {
  if (id == null) return true;
  const n = String(id).trim().toLowerCase();
  return n === "" || n === "0" || n === "null" || n === "undefined";
};

/**
 * PO line `remarks` often holds the product name, but stock-apply overwrites it with
 * "Inventory was adjusted on: …" / "… | Applied N on …". Strip those stamps for display/billing.
 */
export const cleanInventoryProductName = (raw) => {
  if (raw == null) return "";
  let name = String(raw).trim();
  if (!name || name.toLowerCase() === "null") return "";
  name = name.split(/\s*\|\s*Applied\s+/i)[0].trim();
  name = name.split(/\s*\|\s*Already applied\s+/i)[0].trim();
  name = name.split(/\s*\|\s*remaining\s+/i)[0].trim();
  if (/^ALREADY APPLIED/i.test(name)) return "";
  if (/^Already applied on:/i.test(name)) return "";
  if (/^Inventory was adjusted on:/i.test(name)) return "";
  if (/^Applied on:/i.test(name)) return "";
  return name;
};

// ─── Goods Receipt ─────────────────────────────────────────────────

export const getGoodsReceipts = async ({
  purchaseOrderId,
  status,
  start,
  size,
  silent = true,
  hideLoader = true,
} = {}) => {
  const params = {};
  if (purchaseOrderId) params.PurchaseOrder_Id = purchaseOrderId;
  if (status) params.status = status;
  if (start != null) params.start = start;
  if (size != null) params.size = size;
  const data = await apiClient.get(`${STOCK}/goodsReceipts`, {
    params,
    context: "Loading Goods Receipts",
    silent,
    hideLoader,
  });
  return toArray(data).map((g) => ({
    ...g,
    POID: getId(g),
    id: getId(g),
    grnNumber: g.grnNumber || (getId(g) ? `GRN-${getId(g)}` : ""),
    status: String(g.status || "DRAFT").toUpperCase(),
  }));
};

export const getGoodsReceiptById = async (id) => {
  const data = await apiClient.get(`${STOCK}/goodsReceipt/${id}`, {
    context: "Loading Goods Receipt",
  });
  const grn = Array.isArray(data) ? data[0] : data;
  const nested = toArray(grn?.goodsReceiptLines || grn?.lineItems);
  let lines = nested;
  if (lines.length === 0) {
    const fetched = await apiClient.get(`${STOCK}/goodsReceiptLine`, {
      params: { GoodsReceipt_Id: id },
      context: "Loading GRN Lines",
      silent: true,
    });
    lines = toArray(fetched).filter(
      (l) => String(l.GoodsReceipt_Id || l.goodsReceipt_Id || "") === String(id)
    );
  }
  return {
    ...grn,
    POID: getId(grn),
    id: getId(grn),
    lineItems: lines,
  };
};

export const createGoodsReceipt = async (data) => {
  const body = {
    PurchaseOrder_Id: data.PurchaseOrder_Id ? String(data.PurchaseOrder_Id) : null,
    Vendor_Id: data.Vendor_Id ? String(data.Vendor_Id) : null,
    branchId: data.branchId || "BR_HEAD",
    notes: data.notes || "",
    status: data.status || "DRAFT",
    grnNumber: data.grnNumber || undefined,
  };
  const createResp = await apiClient.post(`${STOCK}/goodsReceipt`, body, {
    context: "Creating Goods Receipt",
  });
  let grnId = createResp?.id ?? createResp?.POID;
  if (isInvalidId(grnId) && createResp?.grnNumber) {
    const listed = await getGoodsReceipts({ purchaseOrderId: data.PurchaseOrder_Id });
    const match = listed.find((g) => g.grnNumber === createResp.grnNumber || g.grnNumber === body.grnNumber);
    grnId = match?.POID || match?.id;
  }
  if (isInvalidId(grnId)) {
    const listed = await getGoodsReceipts({ purchaseOrderId: data.PurchaseOrder_Id });
    grnId = listed[0]?.POID || listed[0]?.id;
  }
  if (isInvalidId(grnId)) throw new Error("Goods receipt created but id was invalid");

  const lines = data.lineItems || [];
  for (let i = 0; i < lines.length; i++) {
    const item = lines[i];
    if (!item.productId && !item.productName) continue;
    await apiClient.post(
      `${STOCK}/goodsReceiptLine`,
      {
        GoodsReceipt_Id: String(grnId),
        productId: String(item.productId || ""),
        productName: cleanInventoryProductName(item.productName) || item.productName || "",
        purchaseOrderItemId: item.purchaseOrderItemId ? String(item.purchaseOrderItemId) : null,
        unitOfMeasure: item.unitOfMeasure || "Nos",
        orderedQty: Number(item.orderedQty) || 0,
        receivedQty: Number(item.receivedQty ?? item.quantity) || 0,
        rejectedQty: Number(item.rejectedQty) || 0,
        remarks: item.remarks || "",
        sortOrder: i,
      },
      { context: "Adding GRN Line", silent: true }
    );
  }
  // Avoid extra GET round-trips; caller only needs the new GRN id after success.
  return {
    ...body,
    ...(createResp && typeof createResp === "object" ? createResp : {}),
    POID: String(grnId),
    id: String(grnId),
    lineItems: lines,
  };
};

export const applyGoodsReceiptInventoryStamp = async (id) => {
  return apiClient.post(`${STOCK}/goodsReceipt/${id}/applyInventory`, {}, {
    context: "Stamping GRN Inventory Applied",
  });
};

/**
 * Industrial GRN submit:
 * Single POS call: create GRN + lines, pre-check PO qty, stock IN, PO updates, stamp.
 */
export const submitGoodsReceiptWithStockApply = async (
  { grnData, applyPayload, purchaseOrderId, poStatus },
  _token
) => {
  const idempotencyKey =
    grnData?.idempotencyKey ||
    (typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `grn-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  const poLines = (applyPayload || [])
    .filter((l) => l && (Number(l.quantity) || 0) > 0)
    .map((l) => ({
      purchaseOrderItemId: String(l.id || l.POID || l.purchaseOrderItemId || ""),
      productId: String(l.Product_Id || l.ProductSpecification_Id || l.productId || ""),
      receivedQty: Number(l.quantity) || 0,
      pricePerUnit: Number(l.pricePerUnit) || 0,
      productName: l.name || l.remarks || l.productName || "",
    }));

  const body = {
    idempotencyKey,
    PurchaseOrder_Id: grnData?.PurchaseOrder_Id
      ? String(grnData.PurchaseOrder_Id)
      : purchaseOrderId
        ? String(purchaseOrderId)
        : null,
    Vendor_Id: grnData?.Vendor_Id ? String(grnData.Vendor_Id) : null,
    branchId: grnData?.branchId || "BR_HEAD",
    notes: grnData?.notes || "",
    grnNumber: grnData?.grnNumber || undefined,
    status: grnData?.status || "DRAFT",
    lineItems: (grnData?.lineItems || []).map((item, i) => ({
      productId: String(item.productId || ""),
      productName: cleanInventoryProductName(item.productName) || item.productName || "",
      purchaseOrderItemId: item.purchaseOrderItemId
        ? String(item.purchaseOrderItemId)
        : null,
      unitOfMeasure: item.unitOfMeasure || "Nos",
      orderedQty: Number(item.orderedQty) || 0,
      receivedQty: Number(item.receivedQty ?? item.quantity) || 0,
      rejectedQty: Number(item.rejectedQty) || 0,
      remarks: item.remarks || "",
      sortOrder: i,
    })),
    poLines,
    purchaseOrderId: purchaseOrderId ? String(purchaseOrderId) : null,
    poStatus: poStatus || undefined,
  };

  const result = await apiClient.post(`${STOCK}/goodsReceipt/receiveWithStockIn`, body, {
    context: "Receiving Goods (GRN + Stock IN)",
  });

  const grnId = result?.goodsReceiptId ?? result?.POID ?? result?.id;
  // Industrial path returns enough fields — skip extra GET (was a major latency source).
  const finalGrn = {
    ...grnData,
    ...(result && typeof result === "object" ? result : {}),
    POID: String(grnId),
    id: String(grnId),
    grnNumber: result?.grnNumber || (grnId ? `GRN-${grnId}` : ""),
    status: String(result?.status || "RECEIVED").toUpperCase(),
    lineItems: grnData?.lineItems,
  };

  return {
    grn: finalGrn,
    applyResult: result,
    purchaseOrderId,
    poStatus,
  };
};

/**
 * Derive already-received / remaining from PO lines in Redux (no extra GRN APIs).
 * applied lines = already received; open lines = remaining.
 */
export const buildOpenReceiveRowsFromPo = (po, products = []) => {
  const resolveProductId = (item) =>
    item?.Product_Id ||
    item?.product_Id ||
    item?.ProductSpecification_Id ||
    item?.productId ||
    "";
  const resolveName = (item) => {
    const pid = resolveProductId(item);
    const product = products.find((p) => String(p.id || p.POID || p.poid) === String(pid));
    if (product?.name || product?.productName) {
      return product.name || product.productName;
    }
    const raw = item?.name || item?.productName || item?.remarks || "";
    const cleaned = cleanInventoryProductName(raw);
    if (cleaned) return cleaned;
    return pid ? `Product #${pid}` : "Product";
  };
  const isApplied = (item) => {
    const status = String(item?.status || "").trim().toLowerCase();
    return status === "applied" || status === "received" || status === "fully_received";
  };

  const allItems = po?.purchaseOrderItems || [];
  const byProduct = {};
  allItems.forEach((item) => {
    const productId = String(resolveProductId(item) || "");
    const qty = Number(item.quantity) || 0;
    if (!productId || qty <= 0) return;
    if (!byProduct[productId]) {
      byProduct[productId] = { ordered: 0, applied: 0, openItems: [] };
    }
    byProduct[productId].ordered += qty;
    if (isApplied(item)) {
      byProduct[productId].applied += qty;
    } else {
      byProduct[productId].openItems.push(item);
    }
  });

  const rows = [];
  Object.keys(byProduct).forEach((productId) => {
    const bucket = byProduct[productId];
    bucket.openItems.forEach((item) => {
      const remainingQty = Number(item.quantity) || 0;
      if (remainingQty <= 0) return;
      rows.push({
        productId,
        productName: resolveName(item),
        purchaseOrderItemId: item.POID || item.id,
        originalOrdered: bucket.ordered,
        orderedQty: bucket.ordered,
        alreadyReceived: bucket.applied,
        remainingQty,
        receivedQty: remainingQty,
        unitOfMeasure: item.unitOfMeasure || "Nos",
        pricePerUnit: Number(item.pricePerUnit) || 0,
      });
    });
  });
  return rows;
};

// ─── Vendor Bill ───────────────────────────────────────────────────

/** Normalize paid/due so pickers never fall back to full total after partial payment. */
export const resolveVendorBillBalances = (bill = {}) => {
  const total = Number(bill.total) || 0;
  let paid = Number(bill.paidAmount);
  if (!Number.isFinite(paid) || paid < 0) paid = 0;
  let due = bill.dueAmount != null && bill.dueAmount !== "" ? Number(bill.dueAmount) : NaN;
  if (!Number.isFinite(due)) {
    due = Math.max(0, total - paid);
  } else {
    due = Math.max(0, due);
  }
  // Prefer total − paid when stored due still equals full total but some amount was paid.
  if (paid > 0.009 && Math.abs(due - total) < 0.009 && total > 0) {
    due = Math.max(0, total - paid);
  }
  return {
    ...bill,
    paidAmount: paid,
    dueAmount: due,
  };
};

/**
 * Allocations are the source of truth for how much a bill has been paid —
 * bill.paidAmount may be stale when the IMS balance patch is unavailable.
 * Returns { [billId]: allocatedTotal } for every bill of this vendor.
 */
const collectPaidByBill = async (payments) => {
  const paidByBill = {};
  await Promise.all(
    toArray(payments)
      .filter((p) => {
        const s = String(p.status || "").toUpperCase();
        return s !== "CANCELLED" && s !== "CANCELED" && s !== "VOIDED";
      })
      .map(async (p) => {
        const paymentId = getId(p);
        if (isInvalidId(paymentId)) return;
        const allocs = await getVendorPaymentAllocations({ vendorPaymentId: paymentId });
        toArray(allocs).forEach((a) => {
          const billId = String(a.VendorBill_Id || a.vendorBill_Id || "").trim();
          if (!billId) return;
          const remarks = String(a.remarks || "").toUpperCase();
          const amount = Number(a.allocatedAmount) || 0;
          if (remarks.startsWith("VOIDED") || amount <= 0) return;
          paidByBill[billId] = (paidByBill[billId] || 0) + amount;
        });
      })
  );
  return paidByBill;
};

export const getVendorBillPaidMap = async (vendorId) => {
  if (!vendorId) return {};
  return collectPaidByBill(await getVendorPayments({ vendorId }));
};

/** Bill status derived from balances so PAID is never missed on the final payment. */
export const deriveVendorBillStatus = (bill = {}, paid = 0, due = 0) => {
  const current = String(bill.status || "DRAFT").toUpperCase();
  if (current === "CANCELLED") return current;
  if (paid > 0.01) return due <= 0.01 ? "PAID" : "PARTIALLY_PAID";
  return current;
};

const applyPaidToBill = (bill, allocPaid) => {
  const total = Number(bill.total) || 0;
  const storedPaid = Number(bill.paidAmount) || 0;
  const paid = Math.max(storedPaid, Number(allocPaid) || 0);
  const due = Math.max(0, total - paid);
  return {
    ...bill,
    paidAmount: paid,
    dueAmount: due,
    status: deriveVendorBillStatus(bill, paid, due),
  };
};

/**
 * Recompute bill balances from payment allocations (fixes partial payments
 * where only status was stored and dueAmount stayed at the full total).
 */
export const enrichVendorBillsWithPayments = async (bills = [], vendorId) => {
  const base = toArray(bills).map((b) =>
    resolveVendorBillBalances({
      ...b,
      POID: getId(b),
      id: getId(b),
    })
  );
  if (!base.length || !vendorId) return base;

  try {
    const paidByBill = await getVendorBillPaidMap(vendorId);
    return base.map((b) => applyPaidToBill(b, paidByBill[String(getId(b) || "")] || 0));
  } catch (e) {
    console.warn("Could not enrich vendor bills from payments:", e);
    return base;
  }
};

/**
 * Same as {@link enrichVendorBillsWithPayments} for mixed-vendor lists, where a
 * single Vendor_Id filter cannot be used.
 */
export const enrichVendorBillListWithPayments = async (bills = []) => {
  const base = toArray(bills).map((b) =>
    resolveVendorBillBalances({
      ...b,
      POID: getId(b),
      id: getId(b),
    })
  );
  if (!base.length) return base;

  try {
    const paidByBill = await collectPaidByBill(await getVendorPayments());
    return base.map((b) => applyPaidToBill(b, paidByBill[String(getId(b) || "")] || 0));
  } catch (e) {
    console.warn("Could not enrich vendor bill list from payments:", e);
    return base;
  }
};

export const getVendorBills = async ({ vendorId, purchaseOrderId, status } = {}) => {
  const params = {};
  if (vendorId) params.Vendor_Id = vendorId;
  if (purchaseOrderId) params.PurchaseOrder_Id = purchaseOrderId;
  if (status) params.status = status;
  const data = await apiClient.get(`${INV}/vendorBills`, {
    params,
    context: "Loading Vendor Bills",
    silent: true,
  });
  return toArray(data).map((b) =>
    resolveVendorBillBalances({
      ...b,
      POID: getId(b),
      id: getId(b),
      billNumber: b.billNumber || (getId(b) ? `VB-${getId(b)}` : ""),
      status: String(b.status || "DRAFT").toUpperCase(),
      openingBalance:
        Boolean(b.openingBalance) ||
        String(b.billNumber || "").toUpperCase().startsWith("OPENING") ||
        String(b.notes || "").toUpperCase().includes("OPENING BALANCE"),
    })
  );
};

/** Returns the existing bill if supplier invoice # is already used for this vendor. */
export const findDuplicateSupplierInvoice = async (
  vendorId,
  billNumber,
  { excludeBillId } = {}
) => {
  const num = String(billNumber || "").trim();
  if (!vendorId || !num) return null;
  const key = num.toUpperCase();
  const matches = (list) =>
    toArray(list).find((b) => {
      const status = String(b.status || "").toUpperCase();
      if (status === "CANCELLED" || status === "CANCELED" || status === "VOIDED") {
        return false;
      }
      const id = String(getId(b) || "");
      if (excludeBillId && id === String(excludeBillId)) return false;
      if (String(b.Vendor_Id || b.vendor_Id || "") !== String(vendorId)) return false;
      const existing = String(b.billNumber || "").trim();
      return existing && existing.toUpperCase() === key;
    }) || null;

  try {
    const stock = require("../index").default.getState()?.accountsPayableState;
    const fromStore = matches(
      (stock?.vendorBills || []).filter(
        (b) => String(b.Vendor_Id || b.vendor_Id || "") === String(vendorId)
      )
    );
    if (fromStore) return fromStore;
    if (stock?.billsLoaded) return null;
  } catch (_) {
    /* network */
  }

  const data = await apiClient.get(`${INV}/vendorBills`, {
    params: { Vendor_Id: String(vendorId) },
    context: "Checking Supplier Invoice Number",
    silent: true,
    hideLoader: true,
  });
  return matches(data);
};

export const getVendorBillById = async (id, { reconcile = true } = {}) => {
  const data = await apiClient.get(`${INV}/vendorBill/${id}`, {
    context: "Loading Vendor Bill",
  });
  const bill = Array.isArray(data) ? data[0] : data;
  // Prefer nested lines from bill payload (already scoped to this bill).
  const nested = toArray(bill?.vendorBillLines || bill?.lineItems);
  let lines = nested;
  if (lines.length === 0) {
    const fetched = await apiClient.get(`${INV}/vendorBillLine`, {
      params: { VendorBill_Id: id },
      context: "Loading Bill Lines",
      silent: true,
    });
    // Safety: never show other bills' lines if the backend ignores the filter.
    lines = toArray(fetched).filter(
      (l) => String(l.VendorBill_Id || l.vendorBill_Id || "") === String(id)
    );
  }
  const resolved = resolveVendorBillBalances({
    ...bill,
    POID: getId(bill),
    id: getId(bill),
    lineItems: lines.map((l) => ({
      ...l,
      productName:
        cleanInventoryProductName(l.productName) ||
        cleanInventoryProductName(l.remarks) ||
        l.productName ||
        "",
    })),
  });

  if (!reconcile || !resolved.Vendor_Id) return resolved;
  try {
    const paidByBill = await getVendorBillPaidMap(resolved.Vendor_Id);
    return applyPaidToBill(resolved, paidByBill[String(getId(bill) || "")] || 0);
  } catch (e) {
    console.warn("Could not reconcile vendor bill from payments:", e);
    return resolved;
  }
};

export const createVendorBill = async (data) => {
  const body = {
    Vendor_Id: data.Vendor_Id ? String(data.Vendor_Id) : null,
    PurchaseOrder_Id: data.PurchaseOrder_Id ? String(data.PurchaseOrder_Id) : null,
    GoodsReceipt_Id: data.GoodsReceipt_Id ? String(data.GoodsReceipt_Id) : null,
    branchId: data.branchId || "BR_HEAD",
    currencyCode: data.currencyCode || "PKR",
    paymentTerms: data.paymentTerms || "",
    notes: data.notes || "",
    ...(data.billNumber ? { billNumber: String(data.billNumber).trim() } : {}),
    billDate: data.billDate || undefined,
    dueDate: data.dueDate || undefined,
    subTotal: Number(data.subTotal) || 0,
    discountAmount: Number(data.discountAmount) || 0,
    taxAmount: Number(data.taxAmount) || 0,
    total: Number(data.total) || 0,
    status: data.status || "DRAFT",
    lineItems: (data.lineItems || []).map((item, i) => {
      const qty = Number(item.quantity) || 0;
      const unitPrice = Number(item.unitPrice ?? item.pricePerUnit) || 0;
      return {
        productId: String(item.productId || ""),
        productName: cleanInventoryProductName(item.productName) || item.productName || "",
        purchaseOrderItemId: item.purchaseOrderItemId ? String(item.purchaseOrderItemId) : null,
        goodsReceiptLineId: item.goodsReceiptLineId ? String(item.goodsReceiptLineId) : null,
        unitOfMeasure: item.unitOfMeasure || "Nos",
        quantity: qty,
        unitPrice,
        discountAmount: Number(item.discountAmount) || 0,
        taxRate: Number(item.taxRate) || 0,
        taxAmount: Number(item.taxAmount) || 0,
        lineTotal: Number(item.lineTotal) || qty * unitPrice,
        sortOrder: i,
        remarks: item.remarks || "",
      };
    }),
  };

  const result = await apiClient.post(`${INV}/vendorBill/createWithIntegrity`, body, {
    context: "Creating Vendor Bill",
  });
  const billId = result?.billId ?? result?.POID ?? result?.id;
  if (isInvalidId(billId)) throw new Error("Vendor bill created but id was invalid");

  // Snapshot from write — no verify GET-by-id
  return resolveVendorBillBalances({
    ...body,
    ...(result && typeof result === "object" ? result : {}),
    POID: String(billId),
    id: String(billId),
    billNumber:
      result?.billNumber || body.billNumber || (billId ? `VB-${billId}` : ""),
    status: String(result?.status || body.status || "DRAFT").toUpperCase(),
    paidAmount: 0,
    dueAmount: Number(body.total) || 0,
    lineItems: body.lineItems || [],
  });
};

export const updateVendorBillStatus = async (id, statusOrPatch) => {
  const patch =
    typeof statusOrPatch === "string"
      ? { status: statusOrPatch }
      : {
          status: statusOrPatch?.status,
          ...(statusOrPatch?.paidAmount != null
            ? { paidAmount: Number(statusOrPatch.paidAmount) || 0 }
            : {}),
          ...(statusOrPatch?.dueAmount != null
            ? { dueAmount: Number(statusOrPatch.dueAmount) || 0 }
            : {}),
        };
  return apiClient.put(`${INV}/vendorBill/${id}/status`, patch, {
    context: "Updating Vendor Bill Status",
  });
};

export const voidVendorBill = async (billId, reason = "") => {
  if (isInvalidId(billId)) {
    throw new Error("billId is required");
  }
  return apiClient.post(
    `${INV}/vendorBill/void`,
    { billId: String(billId), reason: reason || "Voided by user" },
    { context: "Voiding Vendor Bill" }
  );
};

export const getVendorPaymentAllocations = async ({
  vendorPaymentId,
  vendorBillId,
  silent = true,
  hideLoader = true,
} = {}) => {
  const params = {};
  if (vendorPaymentId) params.VendorPayment_Id = vendorPaymentId;
  if (vendorBillId) params.VendorBill_Id = vendorBillId;
  const data = await apiClient.get(`${PAY}/vendorPaymentAllocation`, {
    params,
    context: "Loading Payment Allocations",
    silent,
    hideLoader,
  });
  return toArray(data);
};

/**
 * Payments that allocate amount to a specific vendor bill.
 * Always verifies VendorBill_Id client-side (backend may ignore the filter param).
 */
export const getVendorPaymentsForBill = async (billId, { vendorId } = {}) => {
  const billKey = String(billId || "").trim();
  if (!billKey) return [];

  const paymentIds = new Set();

  const collectFromAllocs = (allocs) => {
    toArray(allocs).forEach((a) => {
      const allocBill = String(a.VendorBill_Id || a.vendorBill_Id || a.vendorBillId || "").trim();
      if (allocBill !== billKey) return;
      const pid = String(
        a.VendorPayment_Id || a.vendorPayment_Id || a.vendorPaymentId || ""
      ).trim();
      if (pid && pid !== "null" && pid !== "undefined") paymentIds.add(pid);
    });
  };

  try {
    collectFromAllocs(await getVendorPaymentAllocations({ vendorBillId: billKey }));
  } catch (_) {
    /* try per-payment fallback below */
  }

  const payments = await getVendorPayments(vendorId ? { vendorId } : {});
  const list = toArray(payments);

  if (paymentIds.size === 0) {
    await Promise.all(
      list.map(async (p) => {
        const pid = p.POID || p.id;
        if (isInvalidId(pid)) return;
        try {
          collectFromAllocs(
            await getVendorPaymentAllocations({ vendorPaymentId: pid })
          );
        } catch (_) {
          /* ignore */
        }
      })
    );
  }

  return list.filter((p) => paymentIds.has(String(p.POID || p.id || "")));
};

// ─── Vendor Payment ────────────────────────────────────────────────

export const getVendorPayments = async ({ vendorId, status } = {}) => {
  const params = {};
  if (vendorId) params.Vendor_Id = vendorId;
  if (status) params.status = status;
  const data = await apiClient.get(`${PAY}/vendorPayments`, {
    params,
    context: "Loading Vendor Payments",
    silent: true,
  });
  return toArray(data).map((p) => ({
    ...p,
    POID: getId(p),
    id: getId(p),
    paymentNumber: p.paymentNumber || (getId(p) ? `VP-${getId(p)}` : ""),
    status: String(p.status || "DRAFT").toUpperCase(),
  }));
};

const todayYmd = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const createVendorPayment = async (data) => {
  // paymentDate is the AP business date (yyyy-MM-dd); createdAt remains system audit stamp.
  const paymentDateRaw = data.paymentDate != null ? String(data.paymentDate).trim() : "";
  const paymentDate =
    paymentDateRaw.length >= 10 ? paymentDateRaw.substring(0, 10) : todayYmd();

  const idempotencyKey =
    data.idempotencyKey ||
    (typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `vp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  const body = {
    Vendor_Id: data.Vendor_Id ? String(data.Vendor_Id) : null,
    branchId: data.branchId || "BR_HEAD",
    currencyCode: data.currencyCode || "PKR",
    paymentMethod: data.paymentMethod || "CASH",
    notes: data.notes || "",
    referenceNumber: data.referenceNumber || "",
    paymentDate,
    totalAmount: Number(data.totalAmount) || 0,
    status: data.status || "POSTED",
    idempotencyKey,
    allocations: (data.allocations || [])
      .filter((a) => a?.VendorBill_Id && Number(a.allocatedAmount) > 0)
      .map((a, i) => ({
        VendorBill_Id: String(a.VendorBill_Id),
        allocatedAmount: Number(a.allocatedAmount) || 0,
        remarks: a.remarks || "",
        sortOrder: a.sortOrder != null ? a.sortOrder : i,
      })),
  };

  const result = await apiClient.post(
    `${PAY}/vendorPayment/recordWithAllocations`,
    body,
    { context: "Creating Vendor Payment" }
  );
  const paymentId = result?.paymentId ?? result?.id ?? result?.POID;
  if (isInvalidId(paymentId)) {
    throw new Error("Vendor payment created but id was invalid");
  }
  // Snapshot from write — no list refetch / verify GET
  return {
    ...body,
    ...(result && typeof result === "object" ? result : {}),
    POID: String(paymentId),
    id: String(paymentId),
    paymentId: String(paymentId),
    paymentNumber:
      result?.paymentNumber || (paymentId ? `VP-${paymentId}` : ""),
    status: String(result?.status || body.status || "POSTED").toUpperCase(),
    allocations: body.allocations || [],
  };
};

export const voidVendorPayment = async (paymentId, reason = "") => {
  if (isInvalidId(paymentId)) {
    throw new Error("paymentId is required");
  }
  return apiClient.post(
    `${PAY}/vendorPayment/void`,
    { paymentId: String(paymentId), reason: reason || "Voided by user" },
    { context: "Voiding Vendor Payment" }
  );
};

// ─── Quality Inspection ────────────────────────────────────────────

export const getQualityInspections = async ({
  goodsReceiptId,
  status,
  start,
  size,
  silent = true,
  hideLoader = true,
} = {}) => {
  const params = {};
  if (goodsReceiptId) params.GoodsReceipt_Id = goodsReceiptId;
  if (status) params.status = status;
  if (start != null) params.start = start;
  if (size != null) params.size = size;
  const data = await apiClient.get(`${STOCK}/qualityInspections`, {
    params,
    context: "Loading Quality Inspections",
    silent,
    hideLoader,
  });
  return toArray(data).map((q) => ({
    ...q,
    POID: getId(q),
    id: getId(q),
    inspectionNumber: q.inspectionNumber || (getId(q) ? `QI-${getId(q)}` : ""),
    status: String(q.status || "DRAFT").toUpperCase(),
    GoodsReceipt_Id: q.GoodsReceipt_Id ?? q.goodsReceipt_Id ?? null,
  }));
};

export const getQualityInspectionById = async (id) => {
  const data = await apiClient.get(`${STOCK}/qualityInspection/${id}`, {
    context: "Loading Quality Inspection",
  });
  const qi = Array.isArray(data) ? data[0] : data;
  const nested = toArray(qi?.qualityInspectionLines || qi?.lineItems);
  let lines = nested;
  if (lines.length === 0) {
    const fetched = await apiClient.get(`${STOCK}/qualityInspectionLine`, {
      params: { QualityInspection_Id: id },
      context: "Loading QI Lines",
      silent: true,
    });
    lines = toArray(fetched).filter(
      (l) =>
        String(l.QualityInspection_Id || l.qualityInspection_Id || "") === String(id)
    );
  }
  return {
    ...qi,
    POID: getId(qi),
    id: getId(qi),
    inspectionNumber: qi?.inspectionNumber || (getId(qi) ? `QI-${getId(qi)}` : ""),
    status: String(qi?.status || "DRAFT").toUpperCase(),
    GoodsReceipt_Id: qi?.GoodsReceipt_Id ?? qi?.goodsReceipt_Id ?? null,
    lineItems: lines,
  };
};

export const createQualityInspection = async (data) => {
  const body = {
    GoodsReceipt_Id: data.GoodsReceipt_Id ? String(data.GoodsReceipt_Id) : null,
    branchId: data.branchId || "BR_HEAD",
    notes: data.notes || "",
    inspectedBy: data.inspectedBy || "",
    inspectedAt: data.inspectedAt || undefined,
    status: data.status || "COMPLETED",
    inspectionNumber: data.inspectionNumber || undefined,
  };
  const createResp = await apiClient.post(`${STOCK}/qualityInspection`, body, {
    context: "Creating Quality Inspection",
  });
  let qiId = createResp?.id ?? createResp?.POID;
  if (isInvalidId(qiId)) {
    const listed = await getQualityInspections({ goodsReceiptId: data.GoodsReceipt_Id });
    qiId = listed[0]?.POID || listed[0]?.id;
  }
  if (isInvalidId(qiId)) throw new Error("Quality inspection created but id was invalid");

  const lines = data.lineItems || [];
  for (let i = 0; i < lines.length; i++) {
    const item = lines[i];
    if (!item.productId && !item.productName) continue;
    const inspectedQty = Number(item.inspectedQty) || 0;
    const passedQty = Number(item.passedQty) || 0;
    const rejectedQty = Number(item.rejectedQty) || 0;
    await apiClient.post(
      `${STOCK}/qualityInspectionLine`,
      {
        QualityInspection_Id: String(qiId),
        productId: String(item.productId || ""),
        productName: cleanInventoryProductName(item.productName) || item.productName || "",
        goodsReceiptLineId: item.goodsReceiptLineId
          ? String(item.goodsReceiptLineId)
          : null,
        inspectedQty,
        passedQty,
        rejectedQty,
        disposition:
          rejectedQty > 0
            ? item.disposition || "RETURN"
            : item.disposition === "ACCEPTED" || !item.disposition
              ? "ACCEPTED"
              : item.disposition,
        remarks: item.remarks || "",
        sortOrder: i,
      },
      { context: "Adding QI Line", silent: true }
    );
  }
  // Snapshot from write — no verify header GET
  return {
    ...body,
    ...(createResp && typeof createResp === "object" ? createResp : {}),
    POID: String(qiId),
    id: String(qiId),
    inspectionNumber: createResp?.inspectionNumber || `QI-${qiId}`,
    status: String(body.status || "COMPLETED").toUpperCase(),
    lineItems: lines,
  };
};

// ─── Purchase Return ───────────────────────────────────────────────

export const getPurchaseReturns = async ({
  goodsReceiptId,
  purchaseOrderId,
  status,
  start,
  size,
  silent = true,
  hideLoader = true,
} = {}) => {
  const params = {};
  if (goodsReceiptId) params.GoodsReceipt_Id = goodsReceiptId;
  if (purchaseOrderId) params.PurchaseOrder_Id = purchaseOrderId;
  if (status) params.status = status;
  if (start != null) params.start = start;
  if (size != null) params.size = size;
  const data = await apiClient.get(`${STOCK}/purchaseReturns`, {
    params,
    context: "Loading Purchase Returns",
    silent,
    hideLoader,
  });
  return toArray(data).map((r) => ({
    ...r,
    POID: getId(r),
    id: getId(r),
    returnNumber: r.returnNumber || (getId(r) ? `PRTN-${getId(r)}` : ""),
    status: String(r.status || "DRAFT").toUpperCase(),
    GoodsReceipt_Id: r.GoodsReceipt_Id ?? r.goodsReceipt_Id ?? null,
    PurchaseOrder_Id: r.PurchaseOrder_Id ?? r.purchaseOrder_Id ?? null,
    Vendor_Id: r.Vendor_Id ?? r.vendor_Id ?? null,
  }));
};

export const getPurchaseReturnById = async (id) => {
  const data = await apiClient.get(`${STOCK}/purchaseReturn/${id}`, {
    context: "Loading Purchase Return",
  });
  const pr = Array.isArray(data) ? data[0] : data;
  const nested = toArray(pr?.purchaseReturnLines || pr?.lineItems);
  let lines = nested;
  if (lines.length === 0) {
    const fetched = await apiClient.get(`${STOCK}/purchaseReturnLine`, {
      params: { PurchaseReturn_Id: id },
      context: "Loading Return Lines",
      silent: true,
    });
    lines = toArray(fetched).filter(
      (l) =>
        String(l.PurchaseReturn_Id || l.purchaseReturn_Id || "") === String(id)
    );
  }
  return {
    ...pr,
    POID: getId(pr),
    id: getId(pr),
    returnNumber: pr?.returnNumber || (getId(pr) ? `PRTN-${getId(pr)}` : ""),
    status: String(pr?.status || "DRAFT").toUpperCase(),
    GoodsReceipt_Id: pr?.GoodsReceipt_Id ?? pr?.goodsReceipt_Id ?? null,
    PurchaseOrder_Id: pr?.PurchaseOrder_Id ?? pr?.purchaseOrder_Id ?? null,
    Vendor_Id: pr?.Vendor_Id ?? pr?.vendor_Id ?? null,
    lineItems: lines,
  };
};

/**
 * Sum already-returned quantities for a GRN (industrial: returnable = received − returned).
 * Keys: goodsReceiptLineId (preferred) and productId (fallback).
 * Redux-first: use purchaseReturns in store; hole-fill thin returns only.
 */
export const getAlreadyReturnedQtyByGrn = async (goodsReceiptId) => {
  const byGrnLineId = {};
  const byProductId = {};
  if (isInvalidId(goodsReceiptId)) {
    return { byGrnLineId, byProductId };
  }
  const grnKey = String(goodsReceiptId);

  let returns = [];
  try {
    const stock = require("../index").default.getState()?.purchaseStockState;
    returns = (stock?.purchaseReturns || []).filter(
      (r) => String(r?.GoodsReceipt_Id || r?.goodsReceipt_Id || "") === grnKey
    );
  } catch (_) {
    /* fall through to network */
  }

  if (!returns.length) {
    returns = (await getPurchaseReturns({ goodsReceiptId: grnKey })).filter(
      (r) => String(r?.GoodsReceipt_Id || r?.goodsReceipt_Id || "") === grnKey
    );
  }

  for (const ret of returns) {
    const rid = ret?.POID || ret?.id;
    if (isInvalidId(rid)) continue;
    let lines = toArray(ret?.lineItems || ret?.purchaseReturnLines);
    if (lines.length === 0) {
      try {
        const detail = await getPurchaseReturnById(rid);
        lines = toArray(detail?.lineItems);
      } catch {
        lines = [];
      }
    }
    for (const line of lines) {
      const qty = Number(line?.returnedQty) || 0;
      if (qty <= 0) continue;
      const grnLineId = String(
        line?.goodsReceiptLineId || line?.GoodsReceiptLine_Id || ""
      ).trim();
      const productId = String(line?.productId || "").trim();
      if (grnLineId && grnLineId !== "0") {
        byGrnLineId[grnLineId] = (byGrnLineId[grnLineId] || 0) + qty;
      }
      if (productId && productId !== "0") {
        byProductId[productId] = (byProductId[productId] || 0) + qty;
      }
    }
  }
  return { byGrnLineId, byProductId };
};

export const getPurchaseOrderReturnContext = async (purchaseOrderId) => {
  const empty = {
    grnId: "",
    grnIds: [],
    hasGoodsReceipt: false,
    hasReturnable: false,
    fullyReturned: false,
  };
  if (isInvalidId(purchaseOrderId)) return empty;

  const poKey = String(purchaseOrderId);

  let grns = [];
  try {
    const stock = require("../index").default.getState()?.purchaseStockState;
    grns = (stock?.goodsReceipts || []).filter(
      (g) =>
        String(g.PurchaseOrder_Id || g.purchaseOrder_Id || g.purchaseOrderId || "") ===
        poKey
    );
  } catch (_) {
    /* network */
  }

  if (!grns.length) {
    grns = (await getGoodsReceipts({ purchaseOrderId: poKey })).filter(
      (g) =>
        String(g.PurchaseOrder_Id || g.purchaseOrder_Id || g.purchaseOrderId || "") ===
          poKey || !g.PurchaseOrder_Id
    );
  }

  const linked =
    grns.filter(
      (g) =>
        String(g.PurchaseOrder_Id || g.purchaseOrder_Id || g.purchaseOrderId || "") ===
        poKey
    ).length > 0
      ? grns.filter(
          (g) =>
            String(g.PurchaseOrder_Id || g.purchaseOrder_Id || g.purchaseOrderId || "") ===
            poKey
        )
      : grns;

  if (!linked.length) return empty;

  const sorted = [...linked].sort(
    (a, b) => (Number(b.POID || b.id) || 0) - (Number(a.POID || a.id) || 0)
  );
  const grnIds = sorted.map((g) => String(g.POID || g.id)).filter(Boolean);
  const primaryGrnId = grnIds[0] || "";

  let anyReceived = false;
  let anyReturnable = false;

  for (const gid of grnIds) {
    let lines = [];
    const fromList = linked.find((g) => String(g.POID || g.id) === gid);
    lines = toArray(fromList?.lineItems || fromList?.goodsReceiptLines);
    if (lines.length === 0) {
      try {
        const detail = await getGoodsReceiptById(gid);
        lines = toArray(detail?.lineItems);
      } catch {
        lines = [];
      }
    }
    const already = await getAlreadyReturnedQtyByGrn(gid);
    for (const line of lines) {
      const received = Number(line?.receivedQty) || 0;
      if (received <= 0) continue;
      anyReceived = true;
      const grnLineId = String(line?.POID || line?.id || "").trim();
      const productId = String(line?.productId || "").trim();
      const returned =
        (grnLineId && already.byGrnLineId[grnLineId]) ||
        (productId && already.byProductId[productId]) ||
        0;
      if (received - Number(returned || 0) > 0) anyReturnable = true;
    }
  }

  return {
    grnId: primaryGrnId,
    grnIds,
    hasGoodsReceipt: true,
    hasReturnable: anyReturnable,
    fullyReturned: anyReceived && !anyReturnable,
  };
};

export const applyPurchaseReturnInventoryStamp = async (id, { forceStockOut = false } = {}) => {
  const qs = forceStockOut ? "?forceStockOut=true" : "";
  return apiClient.post(
    `${STOCK}/purchaseReturn/${id}/applyInventory${qs}`,
    {},
    { context: "Applying purchase return stock OUT" }
  );
};

export const createPurchaseReturn = async (data) => {
  const body = {
    GoodsReceipt_Id: data.GoodsReceipt_Id ? String(data.GoodsReceipt_Id) : null,
    PurchaseOrder_Id: data.PurchaseOrder_Id ? String(data.PurchaseOrder_Id) : null,
    Vendor_Id: data.Vendor_Id ? String(data.Vendor_Id) : null,
    branchId: data.branchId || "BR_HEAD",
    reason: data.reason || "",
    notes: data.notes || "",
    returnedAt: data.returnedAt || undefined,
    status: data.status || "DRAFT",
    returnNumber: data.returnNumber || undefined,
    applyInventory: data.applyInventory !== false,
    lineItems: (data.lineItems || [])
      .filter((item) => item && (Number(item.returnedQty) || 0) > 0)
      .map((item, i) => ({
        productId: String(item.productId || ""),
        productName: cleanInventoryProductName(item.productName) || item.productName || "",
        goodsReceiptLineId: item.goodsReceiptLineId
          ? String(item.goodsReceiptLineId)
          : null,
        unitOfMeasure: item.unitOfMeasure || "Nos",
        returnedQty: Number(item.returnedQty) || 0,
        remarks: item.remarks || "",
        sortOrder: i,
      })),
  };

  const createResp = await apiClient.post(`${STOCK}/purchaseReturn/createWithIntegrity`, body, {
    context: "Creating Purchase Return",
  });
  const returnId = createResp?.returnId ?? createResp?.POID ?? createResp?.id;
  if (isInvalidId(returnId)) throw new Error("Purchase return created but id was invalid");

  let stockApplied = false;
  if (data.applyInventory !== false) {
    await applyPurchaseReturnInventoryStamp(returnId);
    stockApplied = true;
  }

  // Snapshot from write — no verify header GET
  return {
    ...body,
    ...(createResp && typeof createResp === "object" ? createResp : {}),
    POID: String(returnId),
    id: String(returnId),
    returnNumber: createResp?.returnNumber || `PRTN-${returnId}`,
    status: String(
      (stockApplied ? "RETURNED" : body.status) || "DRAFT"
    ).toUpperCase(),
    stockApplied,
    lineItems: data.lineItems || [],
  };
};

/**
 * Industrial vendor bill basis:
 * billable qty = sum(GRN received) − sum(purchase returns) for the PO.
 * Prefer purchaseOrderItemId; fall back to productId.
 * Redux-first GRNs; hole-fill thin line payloads only.
 */
export const getBillableQuantitiesForPurchaseOrder = async (purchaseOrderId) => {
  const byPoItemId = {};
  const byProductId = {};
  if (isInvalidId(purchaseOrderId)) {
    return { byPoItemId, byProductId, hasGoodsReceipt: false };
  }
  const poKey = String(purchaseOrderId);

  let grns = [];
  try {
    const stock = require("../index").default.getState()?.purchaseStockState;
    grns = (stock?.goodsReceipts || []).filter(
      (g) => String(g?.PurchaseOrder_Id || g?.purchaseOrder_Id || "") === poKey
    );
  } catch (_) {
    /* network */
  }

  if (!grns.length) {
    grns = (await getGoodsReceipts({ purchaseOrderId: poKey })).filter(
      (g) => String(g?.PurchaseOrder_Id || g?.purchaseOrder_Id || "") === poKey
    );
  }
  if (grns.length === 0) {
    return { byPoItemId, byProductId, hasGoodsReceipt: false };
  }

  for (const grn of grns) {
    const gid = grn?.POID || grn?.id;
    if (isInvalidId(gid)) continue;
    let lines = toArray(grn?.lineItems || grn?.goodsReceiptLines);
    if (lines.length === 0) {
      try {
        const detail = await getGoodsReceiptById(gid);
        lines = toArray(detail?.lineItems);
      } catch {
        continue;
      }
    }
    let already = { byGrnLineId: {}, byProductId: {} };
    try {
      already = await getAlreadyReturnedQtyByGrn(gid);
    } catch {
      /* treat as zero returned */
    }
    for (const line of lines) {
      const received = Number(line?.receivedQty) || 0;
      if (received <= 0) continue;
      const grnLineId = String(line?.POID || line?.id || "").trim();
      const productId = String(line?.productId || "").trim();
      const poItemId = String(line?.purchaseOrderItemId || "").trim();
      const alreadyQty =
        (grnLineId && already.byGrnLineId[grnLineId]) ||
        (productId && already.byProductId[productId]) ||
        0;
      const billable = Math.max(0, received - Number(alreadyQty || 0));
      if (billable <= 0) continue;
      if (poItemId && poItemId !== "0") {
        byPoItemId[poItemId] = (byPoItemId[poItemId] || 0) + billable;
      }
      if (productId && productId !== "0") {
        byProductId[productId] = (byProductId[productId] || 0) + billable;
      }
    }
  }
  return { byPoItemId, byProductId, hasGoodsReceipt: true };
};

/** Page size used when Sync-ing stock lists into Redux (server start/size). */
export const STOCK_LIST_PAGE_SIZE = 200;

const pageAll = async (fetchPage) => {
  const all = [];
  let start = 0;
  for (let guard = 0; guard < 50; guard++) {
    const chunk = await fetchPage(start, STOCK_LIST_PAGE_SIZE);
    const list = Array.isArray(chunk) ? chunk : [];
    all.push(...list);
    if (list.length < STOCK_LIST_PAGE_SIZE) break;
    start += STOCK_LIST_PAGE_SIZE;
  }
  return all;
};

/** Full sync into caller via returned arrays — dispatch GET_* in UI. */
export const syncQualityInspections = async () =>
  pageAll((start, size) =>
    getQualityInspections({ start, size, silent: true, hideLoader: true })
  );

export const syncPurchaseReturns = async () =>
  pageAll((start, size) =>
    getPurchaseReturns({ start, size, silent: true, hideLoader: true })
  );

export const syncGoodsReceipts = async () =>
  pageAll((start, size) =>
    getGoodsReceipts({ start, size, silent: true, hideLoader: true })
  );
